import { csrfToken } from './utils/csrf';

export class ApiError extends Error {
  status: number;
  error?: string;
  field?: string;

  constructor(status: number, message: string, error?: string, field?: string) {
    super(message);
    this.status = status;
    this.error = error;
    this.field = field;
  }
}

async function handleResponse<T>(response: Response, url: string): Promise<T> {
  const text = await response.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // Response wasn't JSON (e.g., 500 error page)
  }

  if (!response.ok && response.status !== 304) {
    const body = json as Record<string, unknown> | null;
    const message = (body?.error as string | undefined) ?? `Request failed: ${url}`;
    throw new ApiError(
      response.status,
      message,
      body?.error as string | undefined,
      body?.field as string | undefined
    );
  }

  return json as T;
}

function isMockMode(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('mock');
}

type JsonFetchOptions = RequestInit & {
  data?: unknown;
  includeCsrf?: boolean;
};

export async function fetchJson(url: string, options: JsonFetchOptions = {}): Promise<Response> {
  const { data, includeCsrf, headers, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();
  const resolvedHeaders = new Headers(headers);

  if (!resolvedHeaders.has('Accept')) {
    resolvedHeaders.set('Accept', 'application/json');
  }

  if (isMockMode()) {
    const params = new URLSearchParams(
      window.location.search
    );

    resolvedHeaders.set('X-Mock-Data', params.get('mock') || 'true');
  }

  const hasJsonBody = data !== undefined;
  if (hasJsonBody && !resolvedHeaders.has('Content-Type')) {
    resolvedHeaders.set('Content-Type', 'application/json');
  }

  const needsCsrf = includeCsrf ?? (method !== 'GET' && method !== 'HEAD');
  if (needsCsrf && !resolvedHeaders.has('X-CSRF-Token')) {
    resolvedHeaders.set('X-CSRF-Token', csrfToken());
  }

  const body = hasJsonBody ? JSON.stringify(data) : rest.body;

  return fetch(url, { cache: 'no-store', ...rest, headers: resolvedHeaders, body });
}

export async function responseJson<T>(response: Response): Promise<T> {
  const json: unknown = await response.json();
  return json as T;
}

async function apiGet<T>(url: string): Promise<T> {
  const response = await fetchJson(url);
  return handleResponse<T>(response, url);
}

async function apiPost<T>(url: string, data: unknown): Promise<T> {
  const response = await fetchJson(url, { method: 'POST', data });
  return handleResponse<T>(response, url);
}

async function apiPut<T>(url: string, data: unknown): Promise<T> {
  const response = await fetchJson(url, { method: 'PUT', data });
  return handleResponse<T>(response, url);
}

async function apiPatch<T>(url: string, data: unknown): Promise<T> {
  const response = await fetchJson(url, { method: 'PATCH', data });
  return handleResponse<T>(response, url);
}

async function apiDel<T>(url: string): Promise<T> {
  const response = await fetchJson(url, { method: 'DELETE' });
  return handleResponse<T>(response, url);
}

export const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  del: apiDel,
};

export function formatError(error: unknown, fallback = 'Something went wrong.'): string {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const apiError = error as ApiError;
  return apiError.error || apiError.message || fallback;
}

export function errorField(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;

  return (error as ApiError).field;
}
